import React, { useEffect, useRef, useState } from "react";
import { ModalDialog } from "react-bootstrap";
import styled from "styled-components";
import { colors } from "utils";
import { LogLevel, logEducatorDashboard } from "utils/sentryLogger";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { PrimaryButtonCenteredLabel, primaryButtonStyles } from "components/pages/Educator/Dashboard/PrimaryButton";
import { SESSION_LENGTH_OPTIONS } from "components/pages/Educator/Dashboard/AssignmentAccordion";
import { CloseIcon, ChevronDownIcon, InfoIcon, MinusIcon, PlusIcon } from "./icons";
import { SAVE_FAILURE, createWarmUpGroups, fetchFreeCodes, saveFailureOf } from "./warmUpApi";
import { newRequestId } from "./requestId";
import { trackWarmUpGroupsSaved } from "./warmUpAnalytics";
import { Message, SelectFrame, WHITE, interText, nativeSelect } from "./styles";
import {
  Body,
  ButtonsRow,
  CloseButton,
  Directions,
  HeaderRow,
  InnerColumn,
  SetNameBlock,
  Title,
  WarmUpModal,
  WideSecondaryButton,
} from "./modalParts";

const TITLE_ID = "warm-up-assign-groups-title";
const GROUPS_LABEL_ID = "warm-up-assign-groups-label";
const SESSION_SELECT_ID = "warm-up-assign-session-length";
const SESSION_TIP_ID = "warm-up-assign-session-tip";

const DIRECTIONS =
  "Pick the number of groups and how long they'll work together. We recommend 2–5 students per group. Don't worry, you can make changes before the sessions start.";
const SESSION_TIP = "How long each group's warm-up session runs. You can change it later.";
const NO_FREE_CODES = "All group codes in this class are in use.";
const SET_UNAVAILABLE = "This warm-up set is no longer available. Pick another one.";
const NOT_SAVED = "Your groups were not saved. Try again.";

const NOT_ENOUGH_CODES_UNCOUNTED = "Not enough group codes are free in this class. Pick fewer groups and try again.";

// After a 409 the free codes are read again; if that read fails there is no
// count to name.
const notEnoughCodesMessage = (free) => {
  if (free === CODES_UNKNOWN) return NOT_ENOUGH_CODES_UNCOUNTED;
  if (free === 0) return NO_FREE_CODES;
  return free === 1 ? "Only 1 group code is free in this class." : `Only ${free} group codes are free in this class.`;
};

const MIN_GROUPS = 1;
const MAX_GROUPS = 30;
const DEFAULT_GROUPS = 4;
const DEFAULT_SESSION_LENGTH = 17.5;

// Free codes: a number once read, null while loading, or "unknown" after a
// failed read (the stepper then runs within 1-30 and the server guards).
const CODES_UNKNOWN = "unknown";

const useFreeCodes = (classroomId) => {
  const [freeCodes, setFreeCodes] = useState(null);
  const [reads, setReads] = useState(0);

  useEffect(() => {
    let active = true;
    setFreeCodes(null);
    fetchFreeCodes(classroomId)
      .then(({ free_codes: free }) => active && setFreeCodes(free))
      .catch(() => active && setFreeCodes(CODES_UNKNOWN));
    return () => {
      active = false;
    };
  }, [classroomId, reads]);

  return { freeCodes, reread: () => setReads((count) => count + 1) };
};

const maxGroupsFor = (freeCodes) => (freeCodes === CODES_UNKNOWN ? MAX_GROUPS : Math.min(freeCodes, MAX_GROUPS));

// The group count: 4 while the free codes load, then min(4, maximum); a
// later read (after a 409) clamps the current value to the new maximum.
// Between 1 and the maximum, or 0 when no code is free.
const useGroupCount = (freeCodes) => {
  const [count, setCount] = useState(DEFAULT_GROUPS);
  const firstRead = useRef(true);
  const known = freeCodes !== null;
  const max = known ? maxGroupsFor(freeCodes) : DEFAULT_GROUPS;

  useEffect(() => {
    if (!known) return;
    const ceiling = maxGroupsFor(freeCodes);
    setCount((value) => Math.min(firstRead.current ? DEFAULT_GROUPS : value, ceiling));
    firstRead.current = false;
  }, [known, freeCodes]);

  return {
    count,
    canIncrease: known && count < max,
    canDecrease: known && count > MIN_GROUPS,
    increase: () => setCount((value) => Math.min(value + 1, max)),
    decrease: () => setCount((value) => Math.max(value - 1, MIN_GROUPS)),
  };
};

// POST /warm-up-groups with one client_request_id per opening: every press
// (and request()'s own retries) reuses it, so a press after a lost 201 replays.
const useSave = ({ set, classroomId, freeCodes, rereadFreeCodes, onSaved, onClassUnreachable }) => {
  const requestId = useRef(null);
  if (requestId.current === null) requestId.current = newRequestId();
  const inFlight = useRef(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);

  const fail = (kind) => {
    inFlight.current = false;
    setSaving(false);
    setFailure(kind);
  };

  const save = ({ groupCount, sessionLength }) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setFailure(null);
    createWarmUpGroups({
      classroom_id: classroomId,
      warmup_set_id: set.id,
      group_count: groupCount,
      session_length: sessionLength,
      client_request_id: requestId.current,
    })
      .then(() => {
        trackWarmUpGroupsSaved(classroomId, set, { groupCount, sessionLength });
        onSaved();
      })
      .catch((error) => {
        const kind = saveFailureOf(error);
        if (kind === SAVE_FAILURE.classUnreachable) {
          onClassUnreachable();
          return;
        }
        if (kind === SAVE_FAILURE.notEnoughCodes) rereadFreeCodes();
        if (kind === SAVE_FAILURE.keyReused) {
          logEducatorDashboard("WarmUpPicker: client_request_id reused for another request", {
            level: LogLevel.WARNING,
            event: "warm_up_groups_key_reused",
            statusCode: error?.status,
            classroomId,
          });
          requestId.current = newRequestId();
        }
        fail(kind);
      });
  };

  const message = (() => {
    if (failure === SAVE_FAILURE.notEnoughCodes) return freeCodes === null ? null : notEnoughCodesMessage(freeCodes);
    if (failure === SAVE_FAILURE.setUnavailable) return SET_UNAVAILABLE;
    if (failure === SAVE_FAILURE.keyReused || failure === SAVE_FAILURE.other) return NOT_SAVED;
    return freeCodes === 0 ? NO_FREE_CODES : null;
  })();

  return { save, saving, message, blocked: failure === SAVE_FAILURE.setUnavailable };
};

export const AssignGroupsModal = ({ set, classroomId, onClose, onSaved, onClassUnreachable }) => {
  const { freeCodes, reread } = useFreeCodes(classroomId);
  const groups = useGroupCount(freeCodes);
  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const { save, saving, message, blocked } = useSave({
    set,
    classroomId,
    freeCodes,
    rereadFreeCodes: reread,
    onSaved,
    onClassUnreachable,
  });
  const canSave = freeCodes !== null && freeCodes !== 0 && !blocked && !saving;
  const close = () => {
    if (!saving) onClose();
  };

  return (
    <WarmUpModal show onHide={close} centered restoreFocus={false} dialogAs={AssignDialog} aria-labelledby={TITLE_ID}>
      <HeaderRow>
        <Title id={TITLE_ID}>Assign Groups</Title>
        <CloseButton type="button" aria-label="Close" onClick={close}>
          <CloseIcon fill={colors.nightlyWoods950} />
        </CloseButton>
      </HeaderRow>
      <Body>
        <InnerColumn>
          <SetNameBlock>{set.name}</SetNameBlock>
          <Directions>{DIRECTIONS}</Directions>
          <Distribution>
            <DistributionRow>
              <LabelBox>
                <FieldLabel id={GROUPS_LABEL_ID}>Groups</FieldLabel>
              </LabelBox>
              <Stepper role="group" aria-labelledby={GROUPS_LABEL_ID}>
                <StepperValue aria-live="polite">{groups.count}</StepperValue>
                <StepperButtons>
                  <StepButton
                    type="button"
                    aria-label="Increase groups"
                    disabled={saving || !groups.canIncrease}
                    onClick={groups.increase}
                  >
                    <PlusIcon stroke={groups.canIncrease ? colors.blue500 : colors.milkGlass800} />
                  </StepButton>
                  <StepButton
                    type="button"
                    aria-label="Decrease groups"
                    disabled={saving || !groups.canDecrease}
                    onClick={groups.decrease}
                  >
                    <MinusIcon stroke={groups.canDecrease ? colors.blue500 : colors.milkGlass800} />
                  </StepButton>
                </StepperButtons>
              </Stepper>
            </DistributionRow>
            <DistributionRow>
              <LabelBox $withIcon>
                <Tooltip id={SESSION_TIP_ID} tip={SESSION_TIP}>
                  <InfoButton type="button" aria-label="About session length" aria-describedby={SESSION_TIP_ID}>
                    <InfoIcon stroke={colors.nightlyWoods500} />
                  </InfoButton>
                </Tooltip>
                <FieldLabel as="label" htmlFor={SESSION_SELECT_ID}>
                  Session Length
                </FieldLabel>
              </LabelBox>
              <SelectFrame>
                <SessionSelect
                  id={SESSION_SELECT_ID}
                  value={sessionLength}
                  disabled={saving}
                  onChange={(event) => setSessionLength(Number(event.target.value))}
                >
                  {SESSION_LENGTH_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </SessionSelect>
                <ChevronDownIcon stroke={colors.blue500} />
              </SelectFrame>
            </DistributionRow>
          </Distribution>
          {message && <Message role="alert">{message}</Message>}
        </InnerColumn>
        <ButtonsRow>
          <WideSecondaryButton type="button" onClick={close}>
            <span>Cancel</span>
          </WideSecondaryButton>
          <SaveButton
            type="button"
            disabled={!canSave}
            aria-busy={saving ? "true" : undefined}
            onClick={() => save({ groupCount: groups.count, sessionLength })}
          >
            <PrimaryButtonCenteredLabel>Save</PrimaryButtonCenteredLabel>
          </SaveButton>
        </ButtonsRow>
      </Body>
    </WarmUpModal>
  );
};

const AssignDialog = styled(ModalDialog)`
  &.modal-dialog {
    --bs-modal-width: 600px;
  }

  & .modal-content {
    background: ${WHITE};
    border: none;
    border-radius: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 32px;
  }
`;

const Distribution = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 32px;
`;

const DistributionRow = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
`;

const LabelBox = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  text-align: right;
  width: 152px;
`;

const FieldLabel = styled.span`
  ${interText(16, 22)}
  margin: 0;
`;

const InfoButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 0;
`;

const Stepper = styled.div`
  align-items: center;
  background: ${WHITE};
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  box-sizing: border-box;
  display: flex;
  height: 48px;
  justify-content: space-between;
  padding: 7px 11px;
  width: 150px;
`;

const StepperValue = styled.span`
  ${interText(16, 22)}
`;

const StepperButtons = styled.div`
  display: flex;
  gap: 16px;
`;

// A 16px icon whose hit box is padded to 24x24 without moving the layout.
const StepButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  margin: -4px;
  padding: 4px;

  &:disabled {
    cursor: default;
  }
`;

const SessionSelect = styled.select`
  ${nativeSelect(150, 48)}
`;

const SaveButton = styled.button`
  ${primaryButtonStyles}
  display: inline-flex;
  justify-content: center;
  padding: 0 16px;
  width: 180px;

  &:disabled {
    background: ${colors.gray700};
    cursor: default;
  }
`;
